import { useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const MEA_BASE_URL = "https://sign.wealthysteward.com/mea";

export type AgreementMember = {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  ontraport_contact_id: string | null;
  una_amount: string | null;
  monthly_amount: string | null;
};

export function buildAgreementUrl(member: AgreementMember): string {
  const name = [member.first_name ?? "", member.last_name ?? ""].join(" ").trim();
  const params = new URLSearchParams({
    name,
    email: member.email ?? "",
    contact_id: member.ontraport_contact_id ?? "",
    una_amount: member.una_amount ?? "",
    monthly_amount: member.monthly_amount ?? "",
  });
  return `${MEA_BASE_URL}?${params.toString()}`;
}

type AgreementPanelProps = {
  member: AgreementMember;
  onPoll: () => void;
};

/**
 * The Member Engagement Agreement signing panel. The portal builds the link and
 * sends the member to DocuSeal in the same tab. Signing is recorded by the
 * webhook, and the page polls so the card flips to signed after return.
 */
export function AgreementPanel({ member, onPoll }: AgreementPanelProps) {
  useEffect(() => {
    const id = window.setInterval(onPoll, 5000);
    return () => window.clearInterval(id);
  }, [onPoll]);

  const url = buildAgreementUrl(member);

  return (
    <div className="space-y-4 rounded-lg border border-border/70 p-4">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">One signature makes it official</h3>
        <p className="text-sm text-muted-foreground">
          While our team reviews your Association's name, this is the perfect moment to sign our
          Member Engagement Agreement. The moment both are done, we get to work making your UNA.
        </p>
      </div>

      <Button asChild className="min-h-11 w-full">
        <a href={url}>Read and sign the agreement</a>
      </Button>

      <p className="text-sm text-muted-foreground">
        The agreement opens full screen. When you finish signing, we bring you right back here.
      </p>
    </div>
  );
}

export function AgreementSignedCard() {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border/70 bg-muted/40 p-4">
      <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
      <div>
        <p className="font-medium">Signed and sealed. Thank you.</p>
      </div>
    </div>
  );
}
